import React from "react";
import { Trash2, ChevronLeft, ChevronRight, Copy, PanelRightClose } from "lucide-react";
import { Slider } from "../../ui/slider";
import { Button } from "../../ui/button";
import { Y, TRANSITIONS, ANIM_IN, ANIM_OUT, FONTS, fontCss, fmtMs } from "./constants";

const Sl = ({ label, value, min, max, step = 1, onChange, testid, show }) => (
  <div>
    <label className="text-sm text-zinc-300">{label}: {show ? show(value) : Math.round(value * 100) / 100}</label>
    <Slider min={min} max={max} step={step} value={[value]} onValueChange={(v) => onChange(v[0])} data-testid={testid}
      className="mt-1.5 [&_[role=slider]]:bg-[color:var(--y)] [&_[role=slider]]:border-0" style={{ "--y": Y }} />
  </div>
);

export const Chips = ({ items, value, onPick, prefix }) => (
  <div className="grid grid-cols-2 gap-1.5">
    {items.map(([v, l]) => (
      <button key={v} onClick={() => onPick(v)} data-testid={`${prefix}-${v}`}
        className={`rounded-lg py-2.5 text-xs font-semibold border ${value === v ? "text-black border-transparent" : "bg-white/5 text-zinc-300 border-white/10 hover:bg-white/10"}`}
        style={value === v ? { background: Y } : {}}>{l}</button>
    ))}
  </div>
);

export const FontPicker = ({ value, onPick, prefix = "font" }) => (
  <div className="grid grid-cols-3 gap-1.5" data-testid={`${prefix}-picker`}>
    {FONTS.map(([k, l]) => (
      <button key={k} onClick={() => onPick(k)} data-testid={`${prefix}-${k.replace(/\s+/g, "-").toLowerCase()}`} title={l}
        className={`rounded-lg py-2 px-1 text-[13px] leading-none truncate border transition-colors ${(value || "default") === k ? "text-black border-transparent" : "bg-white/5 text-zinc-200 border-white/10 hover:bg-white/10"}`}
        style={{ ...fontCss(k), ...((value || "default") === k ? { background: Y } : {}) }}>{l}</button>
    ))}
  </div>
);

const Del = ({ onClick }) => <Button onClick={onClick} variant="outline" data-testid="prop-delete" className="w-full border-red-500/30 bg-transparent text-red-400 hover:bg-red-500/10 rounded-lg text-sm"><Trash2 className="h-3.5 w-3.5 mr-1" />Eliminar</Button>;
const H = ({ children }) => <p className="text-xs uppercase tracking-wider text-zinc-400 font-bold pt-1">{children}</p>;

const Timing = ({ o, upd }) => (
  <div className="grid grid-cols-2 gap-2 text-xs text-zinc-400">
    <div className="rounded bg-white/5 px-2 py-1.5">Inicio <span className="text-white tabular-nums">{fmtMs(o.start)}</span></div>
    <div className="rounded bg-white/5 px-2 py-1.5">Fin <span className="text-white tabular-nums">{fmtMs(o.end)}</span></div>
  </div>
);

const OverlayProps = ({ o, upd, del, dup }) => (
  <div className="space-y-4">
    {o.type === "text" && <textarea value={o.text} onChange={(e) => upd({ text: e.target.value })} data-testid="prop-text" rows={2} className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-base resize-none focus:outline-none focus:border-[color:var(--y)]" style={{ "--y": Y }} />}
    {o.type === "text"
      ? <Sl label="Tamaño" value={o.size} min={12} max={200} onChange={(v) => upd({ size: v })} testid="prop-size" show={Math.round} />
      : <Sl label="Tamaño" value={(o.w || 0.25) * 100} min={5} max={150} onChange={(v) => upd({ w: v / 100 })} testid="prop-size" show={(v) => `${Math.round(v)}%`} />}
    <Sl label="Rotación" value={o.rot || 0} min={-180} max={180} onChange={(v) => upd({ rot: v })} show={(v) => `${Math.round(v)}°`} />
    {o.type === "text" && (
      <div className="flex items-center gap-3">
        <span className="text-sm text-zinc-300">Color</span>
        <input type="color" value={o.color} onChange={(e) => upd({ color: e.target.value })} data-testid="prop-color" className="h-8 w-10 rounded bg-transparent border border-white/10 p-0.5" />
        <label className="flex items-center gap-1.5 text-sm text-zinc-300 ml-auto cursor-pointer"><input type="checkbox" checked={!!o.stroke} onChange={(e) => upd({ stroke: e.target.checked })} data-testid="prop-stroke" className="accent-yellow-400" />Contorno</label>
      </div>
    )}
    {o.type === "text" && (<><H>Fuente</H><FontPicker value={o.font} onPick={(v) => upd({ font: v })} prefix="prop-font" /></>)}
    <Timing o={o} />
    <H>Animación de entrada</H>
    <Chips items={ANIM_IN} value={o.animIn || "none"} onPick={(v) => upd({ animIn: v })} prefix="anim-in" />
    <H>Animación de salida</H>
    <Chips items={ANIM_OUT} value={o.animOut || "none"} onPick={(v) => upd({ animOut: v })} prefix="anim-out" />
    <div className="flex gap-2"><Button onClick={dup} variant="outline" data-testid="prop-duplicate" className="flex-1 border-white/10 bg-transparent rounded-lg text-sm"><Copy className="h-3.5 w-3.5 mr-1" />Duplicar</Button></div>
    <Del onClick={del} />
  </div>
);

const ClipProps = ({ c, k, count, upd, del, move, tab }) => (
  <div className="space-y-4">
    <p className="text-base font-bold truncate">{c.name}</p>
    <div className="grid grid-cols-2 gap-2 text-xs text-zinc-400">
      <div className="rounded bg-white/5 px-2 py-1.5">Entrada <span className="text-white tabular-nums">{fmtMs(c.in)}</span></div>
      <div className="rounded bg-white/5 px-2 py-1.5">Salida <span className="text-white tabular-nums">{fmtMs(c.out)}</span></div>
    </div>
    <Sl label="Velocidad" value={c.speed} min={0.25} max={4} step={0.25} onChange={(v) => upd({ speed: v })} testid="prop-speed" show={(v) => `${v}x`} />
    <div className="flex gap-2">
      <Button onClick={() => move(-1)} disabled={k === 0} variant="outline" data-testid="clip-move-left" className="flex-1 border-white/10 bg-transparent rounded-lg text-sm"><ChevronLeft className="h-3.5 w-3.5" />Antes</Button>
      <Button onClick={() => move(1)} disabled={k === count - 1} variant="outline" data-testid="clip-move-right" className="flex-1 border-white/10 bg-transparent rounded-lg text-sm">Después<ChevronRight className="h-3.5 w-3.5" /></Button>
    </div>
    <H>Transición de entrada</H>
    {k === 0 ? <p className="text-sm text-zinc-500">El primer clip no tiene transición de entrada.</p> : (<>
      <div className={tab === "transition" ? "rounded-lg ring-1 ring-[color:var(--y)] p-1.5" : ""} style={{ "--y": Y }}>
        <Chips items={TRANSITIONS} value={c.transition?.type || "none"} onPick={(v) => upd({ transition: { ...c.transition, type: v } })} prefix="prop-transition" />
      </div>
      {c.transition?.type !== "none" && <Sl label="Duración" value={c.transition?.dur || 0.5} min={0.2} max={2} step={0.1} onChange={(v) => upd({ transition: { ...c.transition, dur: v } })} testid="prop-transition-dur" show={(v) => `${v.toFixed(1)}s`} />}
    </>)}
    <Del onClick={del} />
  </div>
);

const AudioProps = ({ a, upd, del }) => (
  <div className="space-y-4">
    <p className="text-base font-bold truncate">{a.name}</p>
    <Sl label="Volumen" value={a.volume} min={0} max={2} step={0.05} onChange={(v) => upd({ volume: v })} testid="prop-volume" show={(v) => `${Math.round(v * 100)}%`} />
    <Sl label="Fundido de entrada" value={a.fadeIn} min={0} max={5} step={0.1} onChange={(v) => upd({ fadeIn: v })} testid="prop-fadein" show={(v) => `${v.toFixed(1)}s`} />
    <Sl label="Fundido de salida" value={a.fadeOut} min={0} max={5} step={0.1} onChange={(v) => upd({ fadeOut: v })} testid="prop-fadeout" show={(v) => `${v.toFixed(1)}s`} />
    <div className="rounded bg-white/5 px-2 py-1.5 text-xs text-zinc-400">Empieza en <span className="text-white tabular-nums">{fmtMs(a.start)}</span></div>
    <Del onClick={del} />
  </div>
);

export const Inspector = ({ sel, item, k, count, mainVolume, setMainVolume, onUpd, onDel, onDup, onMove, onHide }) => (
  <div className={`order-3 lg:order-none w-full lg:w-[290px] shrink-0 border-t lg:border-t-0 lg:border-l border-white/10 bg-[#1A1A1A] p-4 overflow-y-auto max-h-[50vh] lg:max-h-none ${item ? "block" : "hidden"} lg:block`} data-testid="inspector">
    <div className="flex items-center justify-between mb-4">
      <p className="font-extrabold text-lg">Propiedades</p>
      <button onClick={onHide} title="Ocultar propiedades" data-testid="hide-inspector" className="hidden lg:grid h-8 w-8 place-items-center rounded-lg text-zinc-400 hover:text-white hover:bg-white/10 transition-colors"><PanelRightClose className="h-4 w-4" /></button>
    </div>
    {!item && (<div className="space-y-4">
      <p className="text-sm text-zinc-400 leading-relaxed">Selecciona un clip, texto, sticker o audio para editarlo. Haz clic en un espacio vacío para deseleccionar.</p>
      <Sl label="Volumen del vídeo" value={mainVolume} min={0} max={2} step={0.05} onChange={setMainVolume} testid="main-volume" show={(v) => `${Math.round(v * 100)}%`} />
    </div>)}
    {item && sel.kind === "overlay" && <OverlayProps o={item} upd={onUpd} del={onDel} dup={onDup} />}
    {item && sel.kind === "clip" && <ClipProps c={item} k={k} count={count} upd={onUpd} del={onDel} move={onMove} tab={sel.tab} />}
    {item && sel.kind === "audio" && <AudioProps a={item} upd={onUpd} del={onDel} />}
  </div>
);
